/**
 * Property URL helpers.
 *
 * The app router is mounted with `basename: '/v2'` (see
 * `src/routes/index.tsx`). Anything that goes through React Router's
 * `Link` / `useNavigate` already accounts for that, but anything that
 * builds a fully-qualified URL by hand (clipboard copy, share buttons,
 * meta tags, emailed links) MUST include `/v2` or the resulting link
 * lands on a 404 for the recipient.
 *
 * Centralising those URL builders here keeps the basename in exactly
 * one place — if it ever changes, this is the only file to update.
 */

/**
 * Router basename — must match `basename` in `src/routes/index.tsx`.
 * Kept as a constant rather than imported so this module stays free of
 * circular deps with the router.
 */
export const APP_BASENAME = '/v2';

/** In-router path (no basename, suitable for `<Link to=...>`). */
export function propertyPath(buildingId: number | string, tab?: string): string {
  const base = `/properties/${buildingId}`;
  return tab ? `${base}?tab=${encodeURIComponent(tab)}` : base;
}

/**
 * Property Drawings tab deep link — canonical entry for per-building vault UI.
 * Legacy `/properties/:id/drawings` redirects here so bookmarks and shared links
 * land in the property workspace (header + tab strip), not the old standalone shell.
 */
export function propertyDrawingsPath(
  buildingId: number | string,
  opts?: { floorId?: number | string },
): string {
  const search = new URLSearchParams({ tab: 'drawings' });
  if (opts?.floorId != null) search.set('floorId', String(opts.floorId));
  return `/properties/${buildingId}?${search.toString()}`;
}

/**
 * Reports tab deep link that auto-opens the in-context PDF viewer slide-out.
 * Used by the property list row menu (`View report`).
 */
export function propertyReportPdfViewPath(
  buildingId: number | string,
  reportId: number,
): string {
  const search = new URLSearchParams({
    tab: 'reports',
    reportId: String(reportId),
    viewPdf: '1',
  });
  return `/properties/${buildingId}?${search.toString()}`;
}

/**
 * Route pattern for the chrome-less measurement-report window (FR-300).
 * Matches {@link propertyReportWindowPath}.
 */
export const MEASUREMENT_REPORT_WINDOW_ROUTE =
  '/properties/:buildingId/reports/:reportId/window';

/**
 * In-router path for the report panel only — the same contents as the
 * Reports-tab slide-over, without the app sidebar, property page, or list.
 * `window.open` callers prefix {@link APP_BASENAME}.
 */
export function propertyReportWindowPath(
  buildingId: number | string,
  reportId: number,
): string {
  return `/properties/${buildingId}/reports/${reportId}/window`;
}

/**
 * SSI Report Review opener — legacy `openReportReviewWindow` lands on
 * `my-properties?reportreview=true&bldngId=` (listing + selected building).
 * Tara FR-137: Property Listing, not Tenants. Cindy 08-21: that property only.
 */
export function propertyReportReviewPath(
  buildingId: number | string,
  _reportId?: number,
): string {
  const search = new URLSearchParams({
    reportreview: '1',
    bldngId: String(buildingId),
  });
  return `/properties?${search.toString()}`;
}

/** Floors & Tenants tab deep link — optional floor focus for tenant-listing navigation (DEF-122). */
export function propertyFloorsTenantsPath(
  buildingId: number | string,
  opts?: { reportId?: number | string; floorId?: number | string },
): string {
  const search = new URLSearchParams({ tab: 'floors-tenants' });
  if (opts?.reportId != null) search.set('reportId', String(opts.reportId));
  if (opts?.floorId != null) search.set('floorId', String(opts.floorId));
  return `/properties/${buildingId}?${search.toString()}`;
}

/**
 * Value Opportunity tab deep link with a floor focus (DEF-320).
 *
 * DEF-122's fix pointed the VO floor table's row link at the Floors & Tenants
 * tab — but that leaves the Value Opportunity floor-level listing entirely,
 * landing on the generic tenant grid instead (the exact regression DEF-320
 * reported). "View floor" from Value Opportunity must stay on Value
 * Opportunity and just focus the row — same `floorId` deep-link contract as
 * `propertyFloorsTenantsPath`, different destination tab.
 */
export function propertyValueOpportunityPath(
  buildingId: number | string,
  opts?: { reportId?: number | string; floorId?: number | string },
): string {
  const search = new URLSearchParams({ tab: 'value-opportunity' });
  if (opts?.reportId != null) search.set('reportId', String(opts.reportId));
  if (opts?.floorId != null) search.set('floorId', String(opts.floorId));
  return `/properties/${buildingId}?${search.toString()}`;
}

/**
 * Full shareable URL including origin + basename.
 * Use for copy-to-clipboard, emails, and any place a non-router link is
 * needed. Safe to call in SSR — falls back to relative path if
 * `window` is not available.
 */
export function buildPropertyUrl(
  buildingId: number | string,
  opts?: { tab?: string; reportId?: number | string },
): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const search = new URLSearchParams();
  if (opts?.tab) search.set('tab', String(opts.tab));
  if (opts?.reportId) search.set('reportId', String(opts.reportId));
  const qs = search.toString();
  const path = `${APP_BASENAME}/properties/${buildingId}${qs ? `?${qs}` : ''}`;
  return `${origin}${path}`;
}
